import { z } from 'zod'

/**
 * Agentic tasks (design 10, screen 6): the console's views of code connections, customer repositories,
 * task files, the review gate, delivery and roll-back, verification evidence, quotes, and task evals.
 */

// ---------------------------------------------------------------------------
// Code connections (GitHub App installations) and per-customer repositories
// ---------------------------------------------------------------------------
export interface CodeConnectionView {
  id: string
  provider: 'github'
  account: string
  account_type: string | null
  status: 'active' | 'suspended' | 'deleted'
  repository_selection: string | null
  repos: string[]
  connected_by: string | null
  created_at: string
  updated_at: string
}
export interface CodeConnectionsView {
  /** Whether this cell can start a GitHub App install (slug, OAuth client and private key are set). */
  github: { configured: boolean; install_ready: boolean; webhooks_ready: boolean }
  connections: CodeConnectionView[]
}
export interface CustomerRepositoryView {
  id: string
  connection_id: string
  repo: string
  organisation: { id: string; name: string } | null
  person: { id: string; name: string } | null
  base_branch: string | null
  install_command: string | null
  test_command: string | null
  preview_command: string | null
  is_default: boolean
  created_at: string
}
const command = z.string().max(400).regex(/^[^\n\r`$;|&<>]*$/, 'Use one plain command without shell operators').nullable().optional()
export const CustomerRepositoryInput = z.object({
  connection_id: z.uuid(),
  repo: z.string().regex(/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/, 'Use owner/name'),
  organisation_id: z.uuid().nullable().optional(),
  person_id: z.uuid().nullable().optional(),
  base_branch: z.string().max(200).regex(/^[A-Za-z0-9._/-]+$/).nullable().optional(),
  install_command: command,
  test_command: command,
  preview_command: command,
  is_default: z.boolean().default(true),
}).refine((v) => v.organisation_id || v.person_id, { message: 'Choose the customer (an organisation or a person)', path: ['organisation_id'] })
export type CustomerRepositoryInput = z.infer<typeof CustomerRepositoryInput>

// ---------------------------------------------------------------------------
// Task detail additions
// ---------------------------------------------------------------------------
export interface TaskFileView {
  id: string; name: string; mime: string; size: number; source: 'agent' | 'verification' | 'mockup' | 'document' | 'staff'
  label: string | null; created_at: string; by: string | null
  /** Console API path that returns the file (authenticated). */
  href: string
}
export interface TaskReviewView { required: boolean; status: 'pending' | 'passed' | 'changes_requested'; by: string | null; at: string | null; note: string | null }
export interface TaskDeliveryView {
  status: 'pending' | 'merging' | 'merged' | 'applied' | 'failed' | 'rolling_back' | 'rolled_back'
  merge_sha: string | null
  pr_number: number | null
  attempts: number
  error: string | null
  merged_at: string | null
  /** The roll-back plan, stated before anything goes live (design 10.7). */
  rollback_plan: string | null
  rollback: { pr_url: string | null; status: string; by: string | null; at: string | null; error: string | null } | null
  /** What the company's pipeline did with the merge commit. */
  deploy: { state: 'success' | 'failure' | 'pending' | 'none' | 'unknown'; failing: string[]; checked_at: string | null } | null
}
export interface VerificationCriterion { criterion: string; met: boolean; evidence: string }
export interface TaskVerificationView {
  ok: boolean
  at: string
  model: string | null
  criteria: VerificationCriterion[]
  evidence: {
    diff: { files: number; insertions: number; deletions: number } | null
    tests: { passed: number; failed: number; skipped: number; source: string } | null
    screenshots: string[]           // task file ids
    documents: string[]             // task file ids
    scan: { ok: boolean; findings: ScanFinding[] } | null
    missing: string[]               // evidence that should have been there and was not
  }
}
export interface ScanFinding { rule: string; severity: 'high' | 'medium' | 'low'; file: string | null; line: number | null; detail: string }
export interface TaskQuoteView {
  credits: number
  amount_minor: number | null
  currency: string | null
  note: string | null
  by: string | null
  status: 'draft' | 'sent' | 'accepted' | 'paid' | 'declined' | 'withdrawn'
  payment: { required: boolean; url: string | null; status: 'none' | 'pending' | 'paid' | 'failed' } | null
  sent_at: string | null
}
export interface TaskPlanView { summary: string; steps: string[]; risks: string[]; estimate_hours: number | null; generated_at: string }
export interface TaskDetailMore {
  template_key: string | null
  /** The template's autonomy level (screen 6: "Autonomy for this template"). */
  template_level: number | null
  /** How long the clarifying conversation took, first question to last answer, in minutes. */
  clarified_in_minutes: number | null
  review: TaskReviewView | null
  delivery: TaskDeliveryView | null
  verification: TaskVerificationView | null
  quote_view: TaskQuoteView | null
  plan: TaskPlanView | null
  files: TaskFileView[]
  repository: string | null
  can_rollback: boolean
  can_undo_changes: boolean
  /** Customer-facing changes made by a business task, which "Undo task changes" reverses together. */
  changes_count: number
  /** Eval tasks only: the template, whether it is still waiting to run, and the scored result. */
  eval?: { template_key: string | null; pending: boolean; result: { passed: boolean; checks: TaskEvalCheck[]; at: string } | null } | null
  /** Connectors the task drafted, waiting for an admin to switch them on (B 7.6-07). */
  connectors?: import('./taskConnectors').TaskConnectorView[]
}

export const TaskActionMore = [
  z.object({ action: z.literal('send_quote'), credits: z.number().int().nonnegative(), amount_minor: z.number().int().nonnegative().nullable().optional(), note: z.string().max(2000).optional(), require_payment: z.boolean().optional() }),
  z.object({ action: z.literal('accept_quote'), note: z.string().min(1).max(1000) }),
  z.object({ action: z.literal('withdraw_quote'), reason: z.string().min(1).max(1000) }),
  z.object({ action: z.literal('rollback'), reason: z.string().min(1).max(1000) }),
  z.object({ action: z.literal('retry_delivery') }),
  z.object({ action: z.literal('undo_changes'), reason: z.string().min(1).max(1000) }),
  z.object({ action: z.literal('rerun'), note: z.string().max(1000).optional() }),
] as const

/** Who a task can be assigned to (anyone with the Tasks space who leads work). */
export interface TaskAssigneeView { id: string; name: string; role: string; on_duty: boolean; own_agents_allowed: boolean }

export const AskAgentInput = z.object({ question: z.string().min(3).max(2000), send_to_agent: z.boolean().default(false) })
export interface AskAgentAnswer { answer: string; sources: { kind: string; at: string | null; text: string }[]; sent_to_agent: boolean }

// ---------------------------------------------------------------------------
// Task evals (design 13.6): one template against a test repository, scored.
// ---------------------------------------------------------------------------
export const TaskEvalFixture = z.object({
  name: z.string().min(1),
  /** The customer's request, as it would arrive. */
  request: z.string().min(1),
  /** Answers to the clarifying questions, by required-information key (applied without messaging anyone). */
  answers: z.record(z.string(), z.string()).default({}),
  /** The test repository (owner/name). Public, or reachable through the company's installation. */
  repo: z.string().regex(/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/).optional(),
  base_branch: z.string().optional(),
  commands: z.object({ install: z.string().optional(), test: z.string().optional(), preview: z.string().optional() }).optional(),
  expect: z.object({
    route: z.enum(['ai', 'ai_signoff', 'consultant']).optional(),
    min_tests_passed: z.number().int().nonnegative().optional(),
    files_touched: z.array(z.string()).optional(),
    max_files: z.number().int().positive().optional(),
    verified: z.boolean().default(true),
    /** Business tasks: the actions the agent should plan. */
    actions: z.array(z.string()).optional(),
  }).default({ verified: true }),
})
export type TaskEvalFixture = z.infer<typeof TaskEvalFixture>
export interface TaskEvalCheck { name: string; ok: boolean; detail: string }
export interface TaskEvalResult { template_key: string; fixture: string; task_id: string | null; passed: boolean; checks: TaskEvalCheck[]; minutes: number | null; cost_micros: number }
